import { useEffect, useRef } from "react";

const STEP = 30;                 // grid cell granularity in minutes
const GRID_START = 6 * 60;       // 06:00
const GRID_END = 24 * 60;        // 24:00

const DAYS_EN = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];
const DAYS_AR = ["الأحد", "الاثنين", "الثلاثاء", "الأربعاء", "الخميس", "الجمعة", "السبت"];

const toMin = (hhmm) => parseInt(hhmm.slice(0, 2), 10) * 60 + parseInt(hhmm.slice(3), 10);
const toHHMM = (m) => `${String(Math.floor(m / 60)).padStart(2, "0")}:${String(m % 60).padStart(2, "0")}`;

export const cellKey = (day, mins) => `${day}-${mins}`;

/** Convert stored per-day ranges ([{day, start, end}]) into selected grid cells. */
export function scheduleToCells(schedule) {
  const cells = new Set();
  for (const r of schedule || []) {
    const start = Math.max(toMin(r.start), GRID_START);
    const end = Math.min(toMin(r.end), GRID_END);
    for (let m = start; m + STEP <= end; m += STEP) cells.add(cellKey(r.day, m));
  }
  return cells;
}

/** Convert selected grid cells back into per-day contiguous ranges for the API. */
export function cellsToSchedule(cells) {
  const byDay = {};
  for (const k of cells) {
    const [d, m] = k.split("-").map(Number);
    (byDay[d] = byDay[d] || []).push(m);
  }
  const out = [];
  for (const d of Object.keys(byDay).map(Number).sort((a, b) => a - b)) {
    const mins = byDay[d].sort((a, b) => a - b);
    let s = mins[0];
    let prev = mins[0];
    for (let i = 1; i < mins.length; i++) {
      if (mins[i] !== prev + STEP) {
        out.push({ day: d, start: toHHMM(s), end: toHHMM(prev + STEP) });
        s = mins[i];
      }
      prev = mins[i];
    }
    out.push({ day: d, start: toHHMM(s), end: toHHMM(prev + STEP) });
  }
  return out;
}

const COLUMNS = [];
for (let m = GRID_START; m < GRID_END; m += STEP) COLUMNS.push(m);

export default function AvailabilityGrid({ cells, onChange, lang = "en" }) {
  const drag = useRef(null); // { day, from, mode: "add"|"remove", before: Set }
  const dayNames = lang === "ar" ? DAYS_AR : DAYS_EN;
  const L = lang === "ar"
    ? { available: "متاح", unavailable: "غير متاح",
        hint: "انقر أو اسحب عبر الخلايا لتحديد أوقات تواجدك. انقر على خلية محددة لإزالتها. تُولَّد أوقات الحجز للزوار من هذه الخلايا وفقاً لمدة الجلسة المختارة أدناه." }
    : { available: "Available", unavailable: "Unavailable",
        hint: "Click or drag across cells to mark the times you are available. Click a marked cell again to remove it. Visitor bookable slots are generated from these cells using the consultation duration selected below." };

  const applySegment = (day, from, to, mode, before) => {
    const next = new Set(before);
    const lo = Math.min(from, to);
    const hi = Math.max(from, to);
    for (let m = lo; m <= hi; m += STEP) {
      const k = cellKey(day, m);
      if (mode === "add") next.add(k); else next.delete(k);
    }
    onChange(next);
  };

  const onCellDown = (day, mins) => (e) => {
    e.preventDefault();
    const mode = cells.has(cellKey(day, mins)) ? "remove" : "add";
    drag.current = { day, from: mins, mode, before: new Set(cells) };
    applySegment(day, mins, mins, mode, drag.current.before);
  };

  const onCellEnter = (day, mins) => () => {
    const d = drag.current;
    if (!d || day !== d.day) return;
    applySegment(d.day, d.from, mins, d.mode, d.before);
  };

  const onCellKey = (day, mins) => (e) => {
    if (e.key !== " " && e.key !== "Enter") return;
    e.preventDefault();
    const next = new Set(cells);
    const k = cellKey(day, mins);
    if (next.has(k)) next.delete(k); else next.add(k);
    onChange(next);
  };

  useEffect(() => {
    const stop = () => { drag.current = null; };
    window.addEventListener("mouseup", stop);
    window.addEventListener("touchend", stop);
    return () => {
      window.removeEventListener("mouseup", stop);
      window.removeEventListener("touchend", stop);
    };
  }, []);

  return (
    <div data-testid="availability-grid">
      <p className="mb-3 text-xs text-slate-500">{L.hint}</p>
      <div className="mb-3 flex items-center gap-4 text-xs font-semibold text-slate-500">
        <span className="inline-flex items-center gap-1.5">
          <span className="h-3 w-3 rounded-sm bg-gold" data-testid="grid-legend-available" /> {L.available}
        </span>
        <span className="inline-flex items-center gap-1.5">
          <span className="h-3 w-3 rounded-sm border border-slate-200 bg-white" /> {L.unavailable}
        </span>
      </div>
      <div className="overflow-x-auto rounded-lg border border-slate-200" dir="ltr">
        <div className="min-w-[780px] select-none">
          <div className="flex border-b border-slate-200 bg-slate-50">
            <div className="w-24 shrink-0 px-2 py-1.5" />
            <div className="flex flex-1">
              {COLUMNS.map((m) => (
                <div key={m} className="flex-1 border-l border-slate-100 px-0 py-1.5 text-center text-[9px] font-semibold text-slate-400">
                  {m % 60 === 0 ? toHHMM(m).slice(0, 2) : ""}
                </div>
              ))}
            </div>
          </div>
          {dayNames.map((name, day) => (
            <div key={day} className="flex border-b border-slate-100 last:border-b-0" data-testid={`grid-day-${day}`}>
              <div className="flex w-24 shrink-0 items-center px-2 text-xs font-semibold text-navy">{name}</div>
              <div className="flex flex-1">
                {COLUMNS.map((m) => {
                  const selected = cells.has(cellKey(day, m));
                  return (
                    <button
                      type="button"
                      key={m}
                      data-testid={`grid-cell-${day}-${toHHMM(m).replace(":", "")}`}
                      aria-pressed={selected}
                      aria-label={`${name} ${toHHMM(m)}`}
                      onMouseDown={onCellDown(day, m)}
                      onMouseEnter={onCellEnter(day, m)}
                      onKeyDown={onCellKey(day, m)}
                      className={`h-8 flex-1 border-l border-slate-100 transition-colors focus:outline-none focus:ring-1 focus:ring-inset focus:ring-gold ${
                        selected ? "bg-gold hover:bg-gold-hover" : "bg-white hover:bg-gold-soft"
                      }`}
                    />
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
